import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from './components/ui/sonner';
import { CollectionProvider } from './context/CollectionContext';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ShopItemDetailPage from './pages/ShopItemDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CheckoutCancelPage from './pages/CheckoutCancelPage';
import LearnPage from './pages/LearnPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import MineralDetailPage from './pages/MineralDetailPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import AuthCallback from './pages/AuthCallback';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ReturnsPage from './pages/ReturnsPage';
import ComingSoonPage from './pages/ComingSoonPage';

// Set to true when you want only the Coming Soon page to show
// Set to false to show the full site
const COMING_SOON_MODE = true;

// Router component that handles auth callback detection
function AppRouter() {
    const location = useLocation();
    
    // Check URL fragment for session_id (Google OAuth callback)
    // This must happen synchronously during render, not in useEffect
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    
    // Coming Soon Mode - only show coming soon page and admin
    if (COMING_SOON_MODE) {
        return (
            <Routes>
                <Route path="/" element={<ComingSoonPage />} />
                <Route path="/coming-soon" element={<ComingSoonPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        );
    }
    
    return (
        <>
            {/* Coming Soon page renders without Layout (full-screen) */}
            {location.pathname === '/coming-soon' ? (
                <ComingSoonPage />
            ) : (
                <Layout>
                    <AnimatePresence mode="wait">
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/collection/:id" element={<MineralDetailPage />} />
                            <Route path="/shop" element={<ShopPage />} />
                            <Route path="/shop/:id" element={<ShopItemDetailPage />} />
                            <Route path="/cart" element={<CartPage />} />
                            <Route path="/checkout" element={<CheckoutPage />} />
                            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                            <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
                            <Route path="/learn" element={<LearnPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/privacy" element={<PrivacyPolicyPage />} />
                            <Route path="/terms" element={<TermsOfServicePage />} />
                            <Route path="/returns" element={<ReturnsPage />} />
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route path="/auth/callback" element={<AuthCallback />} />
                            <Route path="/admin" element={<AdminPage />} />
                        </Routes>
                    </AnimatePresence>
                </Layout>
            )}
        </>
    );
}

function App() {
    return (
        <AuthProvider>
            <CollectionProvider>
                <Router>
                    <AppRouter />
                    <Toaster position="bottom-right" />
                </Router>
            </CollectionProvider>
        </AuthProvider>
    );
}

export default App;
